import React, { useState } from 'react';
import { Box } from '@mui/material';
import { TextInput } from '@rentspree/component-2023.components.atoms.text-input';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Create your listing description';
export const stepDescription =
  'Highlight the best features of your property. Write at least 50 characters.';

const ListingDescription: React.FC = () => {
  const [description, setDescription] = useState('');
  const minLength = 50;
  const maxLength = 2000;

  return (
    <Box
      sx={{
        maxWidth: 700,
        margin: '0 auto',
        padding: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <TextInput
          label='Listing Description'
          value={description}
          onChange={e => setDescription(e.target.value)}
          multiline
          rows={8}
          placeholder="Describe your property's best features, amenities, and what makes it special. For example: 'A bright and spacious 3-bedroom apartment with hardwood floors, a newly renovated kitchen, and a private balcony overlooking the city. Located in a quiet neighborhood with excellent restaurants and public transportation nearby...'"
          fullWidth
          inputProps={{
            maxLength: maxLength,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '& textarea': {
                resize: 'vertical',
              },
            },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color:
                description.length >= minLength
                  ? 'success.main'
                  : 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {description.length >= minLength
              ? '✓ Minimum length reached'
              : `${Math.max(0, minLength - description.length)} more characters needed`}
          </Typography>

          <Typography
            variant='body2'
            sx={{
              color:
                description.length > maxLength * 0.9
                  ? 'warning.main'
                  : 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {description.length}/{maxLength}
          </Typography>
        </Box>
      </Box>

      {/* Writing tips */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: '#f8f9fa',
          borderRadius: 2,
          border: '1px solid #e9ecef',
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{
            fontWeight: 600,
            mb: 2,
            color: 'text.primary',
          }}
        >
          💡 Writing Tips
        </Typography>

        <Box component='ul' sx={{ pl: 2, m: 0 }}>
          <Typography
            component='li'
            variant='body2'
            sx={{ mb: 1, color: 'text.secondary' }}
          >
            Mention key features like number of bedrooms, bathrooms, and square
            footage
          </Typography>
          <Typography
            component='li'
            variant='body2'
            sx={{ mb: 1, color: 'text.secondary' }}
          >
            Highlight unique amenities and recent renovations
          </Typography>
          <Typography
            component='li'
            variant='body2'
            sx={{ mb: 1, color: 'text.secondary' }}
          >
            Describe the neighborhood and nearby attractions
          </Typography>
          <Typography
            component='li'
            variant='body2'
            sx={{ color: 'text.secondary' }}
          >
            Use positive, descriptive language to paint a picture
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ListingDescription;
